import { useEffect, useState, useMemo, useRef } from 'react'
import useMobile from '../../../../hooks/useMobile'
import ReactApexChart from 'react-apexcharts'
import dayjs from 'dayjs'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import useYieldGraphContainerStyles from '../Yieldgraph/styles'
import { LinearBuffer } from '../../../PlantTechicalView/components/utils/LinearBuffer'

const YieldGraphContainer = ({
  tableStyle,
  dynamicHeadings,
  mapInverterSeries,
  lineData,
  chartRef,
  height,
  openTable,
  selectedButtons,
  isData,
  isLoading,
}) => {
  const classes = useYieldGraphContainerStyles()
  const isMobile = useMobile()
  const [chartKey, setChartKey] = useState(0)
  const scrollRef = useRef(null)




  // Normalize and align data across all series
  const normalizedMapInverterSeries = useMemo(() => {
    if (!mapInverterSeries || mapInverterSeries.length === 0) return []

    // First, collect all unique valid dates from all series
    const allDatesSet = new Set()
    mapInverterSeries.forEach((series) => {
      if (series.data && Array.isArray(series.data)) {
        series.data.forEach((point) => {
          if (point.x && point.x !== 0 && point.x !== '0' && dayjs(point.x).isValid()) {
            allDatesSet.add(point.x)
          }
        })
      }
    })

    // Convert to sorted array
    const allDates = Array.from(allDatesSet).sort((a, b) => {
      return dayjs(a).valueOf() - dayjs(b).valueOf()
    })

    //Sorted series to ensure consistent order: GTI first, then Yield
    const sortedSeries = [...mapInverterSeries].sort((a, b) => {
      if (a.name === 'GTI') return -1
      if (b.name === 'GTI') return 1
      return 0
    })

    // Process each series to align with all dates
    return sortedSeries.map((series) => {
      if (!series.data || !Array.isArray(series.data)) {
        return {
          ...series,
          data: []
        }
      }

      // Create a map for quick lookup of existing data points
      const dataMap = new Map()
      series.data.forEach((point) => {
        if (point.x && point.x !== 0 && point.x !== '0' && dayjs(point.x).isValid()) {
          let yValue = parseFloat(point.y) || 0

          // Handle extremely small values for GTI
          if (series.name === 'GTI') {
            // If value is extremely small (scientific notation with large negative exponent), treat as 0
            if (yValue < 0.0001 && yValue > 0) {
              yValue = 0
            }
          }

          dataMap.set(point.x, yValue)
        }
      })

      // Create aligned data array with all dates
      const alignedData = allDates.map((date) => ({
        x: date,
        y: dataMap.has(date) ? dataMap.get(date) : null
      }))


      return {
        ...series,
        data: alignedData
      }
    })
  }, [mapInverterSeries])

  // Scroll to right on mobile when data loads
  useEffect(() => {
    if (isMobile && scrollRef.current && normalizedMapInverterSeries.length > 0 && normalizedMapInverterSeries[0].data.length > 0) {
      setTimeout(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollLeft = scrollRef.current.scrollWidth
        }
      }, 500)
    }
  }, [isMobile, normalizedMapInverterSeries])

  // Calculate appropriate Y-axis max
  const getYAxisMax = useMemo(() => {
    if (!normalizedMapInverterSeries || normalizedMapInverterSeries.length === 0) return { gti: 100, yield: 100 }

    let gtiMax = 0
    let specificYieldMax = 0

    normalizedMapInverterSeries.forEach((series) => {
      series.data.forEach((point) => {
        const value = parseFloat(point.y)
        if (!isNaN(value) && value !== null) {
          if (series.name === 'GTI' && value > gtiMax) {
            gtiMax = value;
          } else if (series.name === 'Yield' && value > specificYieldMax) {
            specificYieldMax = value;
          }
        }
      })
    })


    return {
      gti: gtiMax > 0 ? Math.ceil((gtiMax * 1.1) / 6) * 6 : 100,
      yield: specificYieldMax > 0 ? Math.ceil((specificYieldMax * 1.1) / 6) * 6 : 100
    }
  }, [normalizedMapInverterSeries])

  // Handle series synchronization when buttons are selected
  useEffect(() => {
    // Ensure both series are properly aligned when both are selected
    if (selectedButtons?.length === 2 && normalizedMapInverterSeries.length === 2) {
      setChartKey(prevKey => prevKey + 1);
    }
  }, [selectedButtons, normalizedMapInverterSeries]);


  // Enhanced chart options with better tooltip handling
  const chartOptions = useMemo(() => ({
    ...lineData.options,
    chart: {
      ...lineData.options.chart,
      type: 'bar',
      height: height,
      stacked: false,
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
        animateGradually: {
          enabled: true,
          delay: 150,
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350,
        },
      },
      background: '#fff',
      toolbar: {
        show: !isMobile,
        tools: {
          download: true,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true,
        },
        export: {
          csv: {
            filename: 'YieldAndGTI',
            headerCategory: 'Date',
            headerValue: 'value',
          },
          svg: { filename: 'YieldAndGTI' },
          png: { filename: 'YieldAndGTI' },
        },
      },
      zoom: {
        enabled: !isMobile,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '70%',
        endingShape: 'rounded',
        dataLabels: {
          position: 'top',
        },
      },
    },
    legend: {
      show: true,
      position: 'bottom',
      horizontalAlign: 'center',
      floating: false,
      fontSize: '14px',
      fontFamily: 'Helvetica, Arial',
      labels: {
        useSeriesColors: true,
      },
      markers: {
        width: 12,
        height: 12,
        strokeWidth: 0,
        radius: 12,
        offsetX: 0,
        offsetY: 0,
      },
      formatter: function (seriesName) {
        return seriesName === 'Yield' ? 'Specific Yield' : seriesName
      },
      itemMargin: {
        horizontal: 10,
        vertical: 5,
      },
    },
    tooltip: {
      enabled: true,
      shared: true,
      intersect: false,
      x: {
        show: true,
        formatter: function (value, { dataPointIndex }) {
          try {
            // Get the date from the first series data (all series should have aligned dates)
            if (normalizedMapInverterSeries.length > 0 &&
              normalizedMapInverterSeries[0].data[dataPointIndex]) {
              const date = normalizedMapInverterSeries[0].data[dataPointIndex].x
              const parsedDate = dayjs(date)

              if (parsedDate.isValid()) {
                const dateStr = date.toString()
                if (dateStr.length === 7) {
                  return parsedDate.format('MMMM YYYY')
                } else {
                  return parsedDate.format('DD-MMMM-YYYY')
                }
              }
            }
            return 'Invalid Date'
          } catch (e) {
            console.error('Date formatting error in tooltip:', e)
            return 'Date Error'
          }
        },
      },
      y: {
        formatter: function (value, { seriesIndex }) {
          // Get the correct series name
          const seriesName = normalizedMapInverterSeries[seriesIndex]?.name || ''

          if (value === null || value === undefined) {
            return 'No Data'
          }

          const numValue = parseFloat(value)
          if (isNaN(numValue)) return 'N/A'

          // Format based on series type
          if (seriesName === 'GTI') {
            return numValue.toFixed(2) + ' kWh/m²'
          } else if (seriesName === 'Yield') {
            return numValue.toFixed(2) + ' kWh/kWp'
          }

          return numValue.toFixed(2)
        },
      },
    },
    colors: ['#6CB759', '#5968B7'],
    dataLabels: {
      //disabled as it is crowding the chart - try to enable later
      enabled: false,
      formatter: function (val) { return '' },
      offsetY: -20,
      style: {
        fontSize: '10px',
        colors: ["#304758"]
      }
    },
    yaxis: normalizedMapInverterSeries.length === 2
      ? [
        {
          seriesName: 'GTI',
          title: {
            text: 'GTI (kWh/m²)',
            style: { color: '#6CB759', fontSize: '13px' },
          },
          labels: {
            formatter: function (value) {
              return parseFloat(value).toFixed(1)
            },
            style: { colors: '#6CB759' },
          },
          min: 0,
          max: getYAxisMax.gti,
        },
        {
          seriesName: 'Yield',
          opposite: true,
          title: {
            text: 'Specific Yield (kWh/kWp)',
            style: { color: '#5968B7', fontSize: '13px' },
          },
          labels: {
            formatter: function (value) {
              return parseFloat(value).toFixed(1)
            },
            style: { colors: '#5968B7' },
          },
          min: 0,
          max: getYAxisMax.yield,
        },
      ]
      : {
        title: {
          text: selectedButtons?.includes('GTI') ? 'GTI (kWh/m²)' : 'Specific Yield (kWh/kWp)',
          style: {
            color: selectedButtons?.includes('GTI') ? '#6CB759' : '#5968B7',
            fontSize: '13px'
          },
        },
        labels: {
          formatter: function (value) {
            return parseFloat(value).toFixed(1)
          },
        },
        min: 0,
        max: selectedButtons?.includes('GTI') ? getYAxisMax.gti : getYAxisMax.yield,
      },
    xaxis: {
      type: 'category',
      categories: normalizedMapInverterSeries.length > 0
        ? normalizedMapInverterSeries[0].data.map(point => point.x)
        : [],
      labels: {
        rotate: -45,
        rotateAlways: normalizedMapInverterSeries.length > 0 && normalizedMapInverterSeries[0].data.length > 15,
        formatter: function (value) {
          if (!value || value === 0 || value === '0') return ''
          try {
            const parsedDate = dayjs(value)
            if (!parsedDate.isValid()) return value

            const valueStr = value.toString()
            return valueStr.length === 7
              ? parsedDate.format('MM-YYYY')
              : parsedDate.format('DD-MM')
          } catch (e) {
            console.warn('X-axis label formatting error:', e)
            return value
          }
        },
      },
    },
    stroke: {
      width: 0,
    },
    markers: {
      size: 0,
    },
    grid: {
      row: {
        colors: ['#f3f3f3', 'transparent'],
        opacity: 0.5
      }
    },
  }), [lineData, height, normalizedMapInverterSeries, getYAxisMax, selectedButtons, isMobile])

  // Get sorted table data with proper null handling
  const getSortedTableData = useMemo(() => {
    if (!normalizedMapInverterSeries || normalizedMapInverterSeries.length === 0) {
      return []
    }

    // Use the dates from the first series (all should be aligned)
    return normalizedMapInverterSeries[0].data
      .map(point => point.x)
      .filter(date => date && dayjs(date).isValid())
      .sort((a, b) => dayjs(a).valueOf() - dayjs(b).valueOf())
  }, [normalizedMapInverterSeries])

  // Format table values with proper null handling
  const formatTableValue = (value, seriesName) => {
    if (value === undefined || value === null) return 'N/A'

    const numValue = parseFloat(value)
    if (isNaN(numValue)) return 'N/A'

    return numValue.toFixed(2)
  }

  // Get table value for specific date and series
  const getTableValue = (date, seriesName) => {
    const series = normalizedMapInverterSeries.find(s => s.name === seriesName)
    if (!series) return null

    const dataPoint = series.data.find(point => point.x === date)
    return dataPoint ? dataPoint.y : null
  }

  // Debug and refresh effect
  useEffect(() => {

    // Force chart re-render when data changes
    setChartKey(prevKey => prevKey + 1)
  }, [normalizedMapInverterSeries, getSortedTableData, mapInverterSeries])

  return (
    <div className={classes.root}>
      {openTable ? (
        <div className={tableStyle ? classes.tableStyle : classes.tableAllStyle}>
          <TableContainer component={Paper}>
            <Table aria-label="yield and GTI data table">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  {dynamicHeadings?.map((heading, index) => (
                    <TableCell
                      key={index}
                      style={{ textTransform: 'capitalize' }}
                    >
                      {heading === 'Yield' ? 'Specific Yield (kWh/kWp)' : `${heading} (kWh/m²)`}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {getSortedTableData.map((date, rowIndex) => (
                  <TableRow key={rowIndex} hover>
                    <TableCell>
                      {dayjs(date).format('YYYY-MM-DD')}
                    </TableCell>
                    {dynamicHeadings?.map((heading, index) => {
                      const value = getTableValue(date, heading)
                      return (
                        <TableCell key={index}>
                          {formatTableValue(value, heading)}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      ) : isLoading ? (
        <div
          style={{
            padding: '100px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '200px',
            marginTop: '100px',
          }}
        >
          <LinearBuffer />
        </div>
      ) : (
        <div>
          <div className={classes.unitsContainer}>
            {selectedButtons?.includes('GTI') && (
              <div
                style={{
                  color: '#6CB759',
                  fontSize: '12px',
                  marginLeft: '5px',
                }}
              >
                GTI (kWh/m²)
              </div>
            )}
            {selectedButtons?.includes('Yield') && (
              <div
                style={{
                  color: '#5968B7',
                  fontSize: '12px',
                  marginLeft: '5px',
                }}
              >
                Specific Yield (kWh/kWp)
              </div>
            )}
          </div>
          <div id="chart" ref={scrollRef} className={classes.chartContainer} style={{ overflowX: isMobile ? 'auto' : 'visible' }}>
            <div style={{ width: isMobile && normalizedMapInverterSeries.length > 0 ? Math.max(window.innerWidth - 40, normalizedMapInverterSeries[0].data.length * 50) : '100%', minWidth: '100%' }}>
              {normalizedMapInverterSeries.length > 0 && (
                <ReactApexChart
                  key={chartKey}
                  options={chartOptions}
                  ref={chartRef}
                  series={normalizedMapInverterSeries}
                  type="bar"
                  height={height}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default YieldGraphContainer